import React, { useDeferredValue, useEffect, useMemo, useState, useTransition } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Creature, Spell } from '../../types';
import { useReferenceDataStore } from '../../stores/referenceDataStore';
import { useWarbandStore } from '../../stores/warbandStore';
import { formatSoldierType } from '../../utils/formatting';
import { SpellCard } from '../shared/SpellCard';
import { SpellCardMini } from '../shared/SpellCardMini';
import { CreatureCard } from '../shared/CreatureCard';
import { CreatureCardMini } from '../shared/CreatureCardMini';
import { createIceSurface, createParchmentSurface, hashStringToSeed } from '../../theme/surfaces';
import { gridFlexSx, miniFlexSx } from '../../App';

type ListEntry =
  | { type: 'section'; label: string }
  | { type: 'group'; label: string; key: string }
  | { type: 'spell'; spell: Spell }
  | { type: 'creature'; creature: Creature };

const headerItemSx = { width: '100%', gridColumn: '1 / -1' };
const creatureTypeOrder = ['Wizard', 'Apprentice', 'StandardSoldier', 'SpecialistSoldier', 'Creature'];

const getCreatureSearchText = (creature: Creature): string => [
  creature.name,
  formatSoldierType(creature.type),
  creature.notes,
  ...creature.traits.flatMap((trait) => [trait.name, trait.description]),
  ...creature.weapons.map((item) => item.name),
  ...creature.armourItems.map((item) => item.name),
  ...creature.treasureItems.map((item) => item.name),
].filter(Boolean).join(' ').toLowerCase();

export const IndexTab: React.FC = () => {
  const spells = useReferenceDataStore((state) => state.spells);
  const creatures = useReferenceDataStore((state) => state.creatures);
  const isMinified = useWarbandStore((state) => state.isMinified);
  const [, startTransition] = useTransition();
  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let index = 0;
    const ids = [...spells.map((spell) => spell.id), ...creatures.map((creature) => creature.id)];

    const finish = () => {
      if (!cancelled) startTransition(() => setIsReady(true));
    };
    const processChunk = (deadline: IdleDeadline) => {
      if (cancelled) return;
      while (index < ids.length && deadline.timeRemaining() > 1) {
        createParchmentSurface(hashStringToSeed(ids[index]));
        index++;
      }
      if (index < ids.length) requestIdleCallback(processChunk);
      else finish();
    };

    setIsReady(false);
    if (typeof requestIdleCallback !== 'undefined') requestIdleCallback(processChunk);
    else {
      ids.forEach((id) => createParchmentSurface(hashStringToSeed(id)));
      finish();
    }

    return () => { cancelled = true; };
  }, [spells, creatures, startTransition]);

  const words = useMemo(
    () => deferredSearchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean),
    [deferredSearchQuery]
  );
  const filteredSpells = useMemo(() => spells.filter((spell) => {
    const haystack = `${spell.name} ${spell.school} ${spell.description ?? ''}`.toLowerCase();
    return words.every((word) => haystack.includes(word));
  }), [spells, words]);
  const filteredCreatures = useMemo(() => creatures.filter((creature) => {
    const haystack = getCreatureSearchText(creature);
    return words.every((word) => haystack.includes(word));
  }), [creatures, words]);

  const entries = useMemo<ListEntry[]>(() => {
    const result: ListEntry[] = [];
    const sortedSpells = [...filteredSpells].sort((left, right) =>
      left.school.localeCompare(right.school) || left.name.localeCompare(right.name)
    );
    if (sortedSpells.length > 0) {
      result.push({ type: 'section', label: 'Spells' });
      let lastSchool = '';
      sortedSpells.forEach((spell) => {
        if (spell.school !== lastSchool) {
          result.push({ type: 'group', label: spell.school, key: `spell-${spell.school}` });
          lastSchool = spell.school;
        }
        result.push({ type: 'spell', spell });
      });
    }

    const typeRank = (type: string) => {
      const rank = creatureTypeOrder.indexOf(type);
      return rank === -1 ? creatureTypeOrder.length : rank;
    };
    const sortedCreatures = [...filteredCreatures].sort((left, right) =>
      typeRank(left.type) - typeRank(right.type)
      || left.type.localeCompare(right.type)
      || left.name.localeCompare(right.name)
    );
    if (sortedCreatures.length > 0) {
      result.push({ type: 'section', label: 'Creatures' });
      let lastType = '';
      sortedCreatures.forEach((creature) => {
        if (creature.type !== lastType) {
          result.push({ type: 'group', label: formatSoldierType(creature.type, true), key: `creature-${creature.type}` });
          lastType = creature.type;
        }
        result.push({ type: 'creature', creature });
      });
    }
    return result;
  }, [filteredSpells, filteredCreatures]);

  const iceSurface = createIceSurface(10, { icicles: true, card: true });
  const boxSx = isMinified ? miniFlexSx : gridFlexSx;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, ...iceSurface, position: 'sticky', top: 0, zIndex: 10 }} p={1.5}>
        <TextField
          size="small"
          placeholder="Search spells and creatures…"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            },
          }}
          fullWidth
        />
      </Box>

      <Box sx={{ ...iceSurface }} p={1.5}>
        {!isReady ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : entries.length === 0 ? (
          <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
            No spells or creatures match your search.
          </Typography>
        ) : (
          <Box sx={{ ...boxSx, gap: 3 }}>
            {entries.map((entry) => {
              if (entry.type === 'section') {
                return (
                  <Typography key={`section-${entry.label}`} variant="h5" sx={{ ...headerItemSx, fontWeight: 700, mt: 1 }}>
                    {entry.label}
                  </Typography>
                );
              }
              if (entry.type === 'group') {
                return (
                  <Box key={entry.key} sx={headerItemSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="overline" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{entry.label}</Typography>
                      <Divider sx={{ flex: 1 }} />
                    </Box>
                  </Box>
                );
              }
              if (entry.type === 'spell') {
                return isMinified
                  ? <SpellCardMini key={`spell-${entry.spell.id}`} spell={entry.spell} />
                  : <SpellCard key={`spell-${entry.spell.id}`} spell={entry.spell} />;
              }
              return isMinified
                ? <CreatureCardMini key={`creature-${entry.creature.id}`} creature={entry.creature} />
                : <CreatureCard key={`creature-${entry.creature.id}`} creature={entry.creature} />;
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};